import type { TFunction } from "i18next";
import type { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { messages } from "../../../../locales/messages";
import {
  normalizeColumnType,
  operatorNeedsValue,
  type HeaderFilterColumnType,
} from "./filterOperators";

export type ValueOption =
  | string
  | number
  | { value: string | number | boolean; label: string };

export type NormalizedValueOption = {
  value: string | number | boolean;
  label: string;
};

/**
 * Resolve a translation key registered in `locales/messages` to the current language.
 * Used for data-driven filter labels (operators) where a static `t(messages.X())` call
 * isn't possible. Falls back to the raw id if the key is unknown.
 */
export function resolveFilterLabel(t: TFunction, messageId: string): string {
  const accessor = messages[messageId as keyof typeof messages];
  return accessor ? t(accessor()) : messageId;
}

export function normalizeValueOptions(
  valueOptions: readonly ValueOption[] | undefined,
): NormalizedValueOption[] {
  if (!valueOptions) return [];
  return valueOptions.map((opt) => {
    if (typeof opt === "object" && opt !== null && "value" in opt) {
      return { value: opt.value, label: String(opt.label ?? opt.value) };
    }
    return { value: opt, label: String(opt) };
  });
}

/**
 * Human-readable value for an active filter, used by the summary chips. Mirrors the
 * cell inputs: single-select shows the option label (not the raw enum), boolean shows
 * True/False, dates are formatted, and value-less operators (isEmpty/isNotEmpty) show
 * nothing.
 */
export function formatFilterValue(
  t: TFunction,
  params: {
    type: GridColDef["type"];
    operator: string;
    value: unknown;
    valueOptions?: readonly ValueOption[];
  },
): string {
  const { operator, value, valueOptions } = params;
  if (!operatorNeedsValue(operator)) return "";
  if (value === undefined || value === null || value === "") return "";

  const columnType = normalizeColumnType(params.type) as HeaderFilterColumnType;

  switch (columnType) {
    case "boolean":
      return value === true || value === "true"
        ? t(messages.Filter_bool_true())
        : t(messages.Filter_bool_false());

    case "singleSelect": {
      const match = normalizeValueOptions(valueOptions).find(
        (o) => String(o.value) === String(value),
      );
      return match?.label ?? String(value);
    }

    case "date":
    case "dateTime": {
      const d = dayjs(value as string);
      return d.isValid() ? d.format("YYYY-MM-DD") : String(value);
    }

    default:
      return String(value);
  }
}
